// useTreeStepDrag — the ProcessTree drag session (P2 #19): whole-block +
// multi-select "drag by body". Ties the shared threshold-gated `useStepDrag`
// session to the tree's needs:
//   - Every draggable row (leaf step OR container head) registers its DOM node;
//     at drag time those rects become the vertical bands the pure
//     `resolveVerticalDrop` anchors against (pointer hit-testing, NOT a layout
//     decision — no rect ever feeds the model).
//   - A press on a row body is threshold-gated: a sub-threshold release is a
//     CLICK (→ the tree's select handler); past the threshold it drags.
//   - The drop funnels through the shared ProcessStepEditApi — `moveSteps` when
//     the dragged row is part of the multi-selection (the whole selection moves
//     as ONE journaled mutation), else `moveStep` for the single row/block. The
//     engine op already relocates a whole block as a unit and drops carried
//     descendants, so both whole-block and multi drags are one text edit.

import { useCallback, useMemo, useRef } from "react";
import type { NodeId, WalkResult } from "@archlang/engine";
import { useStepDrag, type BodyDownEvent, type StepDropTarget } from "./useStepDrag.js";
import { resolveVerticalDrop, type DragRow } from "./processDragResolve.js";
import { treeDragModel, carriedNodeIds, type TreeDragModel } from "./treeDrag.js";
import type { ProcessStepEditApi } from "./processStepEdit.js";

export interface TreeDragApi {
  /** The nodeId currently being dragged, or null when idle. */
  readonly draggingNodeId: NodeId | null;
  /** The live drop target (drives the insertion line), or null. */
  readonly target: StepDropTarget | null;
  /** Arm a threshold-gated body drag for a draggable row. Selection stays on the
   *  row's own onClick — a real drag swallows the trailing click so it doesn't
   *  also select. */
  readonly onRowBodyDown: (nodeId: NodeId, e: BodyDownEvent) => void;
  /** Ref callback the tree attaches to each draggable row's element — supplies
   *  the rect the drop resolver hit-tests. Passing null unregisters. */
  readonly registerRow: (nodeId: NodeId) => (el: HTMLElement | null) => void;
}

export interface TreeDragOptions {
  readonly walk: WalkResult | null;
  readonly stepEdit?: ProcessStepEditApi;
  /** The current multi-selection — when the dragged row is in it, the WHOLE
   *  selection moves together. */
  readonly selected: ReadonlySet<NodeId>;
}

export function useTreeStepDrag({ walk, stepEdit, selected }: TreeDragOptions): TreeDragApi {
  const model: TreeDragModel = useMemo(() => treeDragModel(walk), [walk]);
  // Live row elements — a ref (not state) so registering never re-renders.
  const rowsRef = useRef(new Map<NodeId, HTMLElement>());
  // Latest selection + model + api, read inside resolve/commit without
  // re-subscribing the window listeners.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const modelRef = useRef(model);
  modelRef.current = model;
  const editRef = useRef(stepEdit);
  editRef.current = stepEdit;

  const registerRow = useCallback(
    (nodeId: NodeId) => (el: HTMLElement | null) => {
      if (el) rowsRef.current.set(nodeId, el);
      else rowsRef.current.delete(nodeId);
    },
    [],
  );

  // The nodeIds this drag is CARRYING: when the dragged row is part of the
  // multi-selection, the whole selection (+ each member's descendants); else
  // just the dragged node (+ its descendants if a block).
  const movingSet = useCallback((dragged: NodeId): readonly NodeId[] => {
    const sel = selectedRef.current;
    return sel.has(dragged) ? [...sel] : [dragged];
  }, []);

  // Ordered bands (client space) of every draggable row, EXCLUDING the carried
  // set (the moving rows can't be their own anchor). Document order comes from
  // the model; a row with no live element yet is skipped.
  const bandsExcluding = useCallback((carried: ReadonlySet<NodeId>): DragRow[] => {
    const out: DragRow[] = [];
    for (const id of modelRef.current.order) {
      if (carried.has(id)) continue;
      const el = rowsRef.current.get(id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      out.push({ nodeId: id, top: r.top, height: r.height });
    }
    return out;
  }, []);

  const drag = useStepDrag({
    resolve: (_clientX, clientY, dragged) => {
      const carried = carriedNodeIds(movingSet(dragged as NodeId), modelRef.current);
      const bands = bandsExcluding(carried);
      // `resolveVerticalDrop` excludes the dragged id itself; the carried filter
      // already removed it + its descendants, so the extra arg is a harmless
      // no-op here. Client Y is the band space (rects are client-space).
      const v = resolveVerticalDrop(bands, clientY, dragged);
      return v ? { kind: "reorder", anchorNodeId: v.anchorNodeId, position: v.position } : null;
    },
    commit: (dragged, t) => {
      if (t.kind !== "reorder") return; // the tree only reorders (no into-zone yet)
      const api = editRef.current;
      if (!api) return;
      const moving = movingSet(dragged as NodeId);
      if (moving.length > 1) {
        api.moveSteps(moving as NodeId[], t.anchorNodeId as unknown as NodeId, t.position);
      } else {
        api.moveStep(dragged as unknown as NodeId, t.anchorNodeId as unknown as NodeId, t.position);
      }
    },
  });

  return {
    draggingNodeId: drag.draggingNodeId as NodeId | null,
    target: drag.target,
    onRowBodyDown: (nodeId, e) => drag.onBodyDown(nodeId, e),
    registerRow,
  };
}
